import type { BudgetData, Category, LineItem, Project } from '../types';
import {
  projectAll,
  type CpiDefaults,
  type ProjectedAmounts,
  type ProjectedLine,
} from './projection';

// All totals are 4-tuples (current + three forward years). Math is kept in
// full float precision; rounding happens only at display time (@cca/ui `fmt`
// and lib/format's fmtMoney).

export type Totals4 = ProjectedAmounts;

export const zeroTotals = (): Totals4 => ({ current: 0, y1: 0, y2: 0, y3: 0 });

export const sumLines = (lines: ProjectedLine[]): Totals4 =>
  lines.reduce(
    (acc, line) => ({
      current: acc.current + line.current,
      y1: acc.y1 + line.y1,
      y2: acc.y2 + line.y2,
      y3: acc.y3 + line.y3,
    }),
    zeroTotals(),
  );

export const addTotals = (a: Totals4, b: Totals4): Totals4 => ({
  current: a.current + b.current,
  y1: a.y1 + b.y1,
  y2: a.y2 + b.y2,
  y3: a.y3 + b.y3,
});

export const subtractTotals = (a: Totals4, b: Totals4): Totals4 => ({
  current: a.current - b.current,
  y1: a.y1 - b.y1,
  y2: a.y2 - b.y2,
  y3: a.y3 - b.y3,
});

export type CategoryGroup = {
  category: Category;
  // Sections can contain both income and expense GL codes — like a
  // Discrete Activity but displayed inline in the operating tables.
  // 'income' sections appear in the Income table; 'expense' sections
  // appear in the Expense table. The subtotal shows the net.
  incomeItems: ProjectedLine[];
  expenseItems: ProjectedLine[];
  archivedIncome: LineItem[];
  archivedExpenses: LineItem[];
  incomeTotals: Totals4;
  expenseTotals: Totals4;
  /** Net to display in the subtotal row:
   *  income section  → incomeTotals − expenseTotals
   *  expense section → expenseTotals − incomeTotals */
  net: Totals4;
};

export type ProjectGroup = {
  project: Project;
  income: ProjectedLine[];
  expenses: ProjectedLine[];
  // Tombstoned rows scoped to this project. Excluded from totals;
  // surfaced when the user reveals archived rows for this project.
  archivedIncome: LineItem[];
  archivedExpenses: LineItem[];
  incomeTotals: Totals4;
  expenseTotals: Totals4;
  net: Totals4;
};

export type DerivedBudget = {
  defaults: CpiDefaults;
  // Full projection, all rows (operating + project)
  incomeProjected: ProjectedLine[];
  expensesProjected: ProjectedLine[];
  // Operating sections (area !== 'financial'), grouped by category, in displayOrder
  incomeByCategory: CategoryGroup[];
  expensesByCategory: CategoryGroup[];
  // Operating-only totals (excludes project items and financial items)
  operatingIncomeTotals: Totals4;
  operatingExpenseTotals: Totals4;
  operatingNet: Totals4;
  // Financial sections (area === 'financial'), grouped by category
  financialIncomeByCategory: CategoryGroup[];
  financialExpensesByCategory: CategoryGroup[];
  financialIncomeTotals: Totals4;
  financialExpenseTotals: Totals4;
  financialNet: Totals4;
  // Per-project, in displayOrder
  projects: ProjectGroup[];
  totalProjectsNet: Totals4;
  // Operating + projects + financial combined
  grandNet: Totals4;
};

const sortByOrder = <T extends { displayOrder: number }>(items: T[]): T[] =>
  [...items].sort((a, b) => a.displayOrder - b.displayOrder);

export const deriveBudget = (data: BudgetData): DerivedBudget => {
  const defaults: CpiDefaults = {
    cpiY1: data.settings.cpiY1Default,
    cpiY2: data.settings.cpiY2Default,
    cpiY3: data.settings.cpiY3Default,
  };

  // Tombstoned rows (soft-deleted) are filtered out before any totals
  // are computed — they don't contribute to subtotals, grand totals,
  // projections, or the visible tables. The Archive panel reads them
  // directly off `working.income` / `working.expenses` instead.
  const incomeProjected = projectAll(
    data.income.filter((i) => !i.deletedAt),
    defaults,
  );
  const expensesProjected = projectAll(
    data.expenses.filter((e) => !e.deletedAt),
    defaults,
  );

  // Tombstoned rows kept aside (raw LineItems, not projected) so the UI
  // can reveal them per-section without polluting totals.
  const archivedIncomeAll = data.income.filter((i) => i.deletedAt);
  const archivedExpensesAll = data.expenses.filter((e) => e.deletedAt);

  const financialCatIds = new Set(
    data.categories.filter((c) => c.area === 'financial').map((c) => c.id),
  );

  // All non-project items, split by area. The pools are passed to
  // makeCategoryGroup which then filters by categoryId, so an income item
  // assigned to an expense section (or vice versa) lands in the right group.
  const opIncomePool = incomeProjected.filter(
    (i) => !i.projectId && !financialCatIds.has(i.categoryId),
  );
  const opExpensePool = expensesProjected.filter(
    (i) => !i.projectId && !financialCatIds.has(i.categoryId),
  );
  const finIncomePool = incomeProjected.filter(
    (i) => !i.projectId && financialCatIds.has(i.categoryId),
  );
  const finExpensePool = expensesProjected.filter(
    (i) => !i.projectId && financialCatIds.has(i.categoryId),
  );

  const makeCategoryGroup = (
    category: Category,
    incomePool: ProjectedLine[],
    expensePool: ProjectedLine[],
  ): CategoryGroup => {
    const incomeItems = sortByOrder(incomePool.filter((i) => i.categoryId === category.id));
    const expenseItems = sortByOrder(expensePool.filter((e) => e.categoryId === category.id));
    const archivedIncome = sortByOrder(
      archivedIncomeAll.filter((i) => i.categoryId === category.id && !i.projectId),
    );
    const archivedExpenses = sortByOrder(
      archivedExpensesAll.filter((e) => e.categoryId === category.id && !e.projectId),
    );
    const incomeTotals = sumLines(incomeItems);
    const expenseTotals = sumLines(expenseItems);
    const net =
      category.type === 'income'
        ? subtractTotals(incomeTotals, expenseTotals)
        : subtractTotals(expenseTotals, incomeTotals);
    return {
      category,
      incomeItems,
      expenseItems,
      archivedIncome,
      archivedExpenses,
      incomeTotals,
      expenseTotals,
      net,
    };
  };

  const incomeCats = sortByOrder(
    data.categories.filter((c) => c.type === 'income' && c.area !== 'financial'),
  );
  const expenseCats = sortByOrder(
    data.categories.filter((c) => c.type === 'expense' && c.area !== 'financial'),
  );
  const financialIncomeCats = sortByOrder(
    data.categories.filter((c) => c.type === 'income' && c.area === 'financial'),
  );
  const financialExpenseCats = sortByOrder(
    data.categories.filter((c) => c.type === 'expense' && c.area === 'financial'),
  );

  const incomeByCategory = incomeCats.map((c) => makeCategoryGroup(c, opIncomePool, opExpensePool));
  const expensesByCategory = expenseCats.map((c) =>
    makeCategoryGroup(c, opIncomePool, opExpensePool),
  );
  const financialIncomeByCategory = financialIncomeCats.map((c) =>
    makeCategoryGroup(c, finIncomePool, finExpensePool),
  );
  const financialExpensesByCategory = financialExpenseCats.map((c) =>
    makeCategoryGroup(c, finIncomePool, finExpensePool),
  );

  // Totals are sums of section nets, not raw item sums.
  const operatingIncomeTotals = incomeByCategory.reduce(
    (a, g) => addTotals(a, g.net),
    zeroTotals(),
  );
  const operatingExpenseTotals = expensesByCategory.reduce(
    (a, g) => addTotals(a, g.net),
    zeroTotals(),
  );
  const operatingNet = subtractTotals(operatingIncomeTotals, operatingExpenseTotals);

  const financialIncomeTotals = financialIncomeByCategory.reduce(
    (a, g) => addTotals(a, g.net),
    zeroTotals(),
  );
  const financialExpenseTotals = financialExpensesByCategory.reduce(
    (a, g) => addTotals(a, g.net),
    zeroTotals(),
  );
  const financialNet = subtractTotals(financialIncomeTotals, financialExpenseTotals);

  const projects: ProjectGroup[] = sortByOrder(data.projects).map((project) => {
    const income = sortByOrder(incomeProjected.filter((i) => i.projectId === project.id));
    const expenses = sortByOrder(expensesProjected.filter((e) => e.projectId === project.id));
    const archivedIncome = sortByOrder(archivedIncomeAll.filter((i) => i.projectId === project.id));
    const archivedExpenses = sortByOrder(
      archivedExpensesAll.filter((e) => e.projectId === project.id),
    );
    const incomeTotals = sumLines(income);
    const expenseTotals = sumLines(expenses);
    return {
      project,
      income,
      expenses,
      archivedIncome,
      archivedExpenses,
      incomeTotals,
      expenseTotals,
      net: subtractTotals(incomeTotals, expenseTotals),
    };
  });

  const totalProjectsNet = projects.reduce((acc, p) => addTotals(acc, p.net), zeroTotals());
  const grandNet = addTotals(addTotals(operatingNet, totalProjectsNet), financialNet);

  return {
    defaults,
    incomeProjected,
    expensesProjected,
    incomeByCategory,
    expensesByCategory,
    operatingIncomeTotals,
    operatingExpenseTotals,
    operatingNet,
    financialIncomeByCategory,
    financialExpensesByCategory,
    financialIncomeTotals,
    financialExpenseTotals,
    financialNet,
    projects,
    totalProjectsNet,
    grandNet,
  };
};
